// Espejo exacto del enum Role del backend (domain/user/Role.java) — Role.of() rechaza
// cualquier otro nombre con ROLE_INVALID, así que acá no puede haber valores de más.
//
// Única fuente de nombres de rol de todo AD (ADR 0014 de credlap-architecture, "los nombres
// de roles viven en un solo módulo de cada consola"): el resto del código usa `ROLES.*` o el
// tipo `Role`, nunca el literal de texto. `tools/role-literals.test.mjs` (parte de
// `npm run verify` vía `test:tools`) falla si un literal de rol aparece fuera de este archivo.
export const ROLES = {
  ADMIN: 'ADMIN',
  OPERATOR: 'OPERATOR',
  COMMS_OPERATOR: 'COMMS_OPERATOR',
  METRICS_VIEWER: 'METRICS_VIEWER',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

// Espejo de credlap-system `RoleMatrix.Tier` (roadmap #14). La JWT trae el rol literal
// (ROLE_COMMS_OPERATOR no se expande a OPERATOR), así que cada tier lista todos sus roles.
// Rutas y navegación usan estas constantes para no divergir de la matriz del backend.

/** OPS: operación diaria (clientes, solicitudes, centro operativo, lecturas de comunicación). */
export const OPS_ROLES: readonly Role[] = [ROLES.ADMIN, ROLES.OPERATOR, ROLES.COMMS_OPERATOR];

/** COMMS: preparar/enviar/programar comunicaciones masivas. */
export const COMMS_ROLES: readonly Role[] = [ROLES.ADMIN, ROLES.COMMS_OPERATOR];

/** CONFIG: configuración de plataforma y datos en reposo. */
export const CONFIG_ROLES: readonly Role[] = [ROLES.ADMIN];

/**
 * COLLECTIONS: who sees the Cobranzas area of the sidebar (ADR 0021 of credlap-architecture,
 * section 4): the roles with permissions in the portfolio admin console (PFA,
 * `src/app/core/authz/permissions.ts` there). Showing the area grants nothing: CS still decides
 * every call, so a drift between the two lists leads at worst to an empty screen or a 403.
 */
export const COLLECTIONS_ROLES: readonly Role[] = [ROLES.ADMIN, ROLES.OPERATOR];

/**
 * Who exports the Personas list (1.23, "operador y administrador"): mirror of the CS rule for the export
 * engine's `/admin/exports` routes (`RoleMatrix` PORTFOLIO_OPS). COMMS_OPERATOR reads Personas but gets 403 there.
 */
export const PERSONS_EXPORT_ROLES: readonly Role[] = [ROLES.ADMIN, ROLES.OPERATOR];

/**
 * Who creates a person from the panel (1.1, "operador y administrador"): mirror of the CS rule for
 * `POST /admin/client-actions` (`RoleMatrix` PORTFOLIO_OPS).
 */
export const PERSON_CREATION_ROLES: readonly Role[] = [ROLES.ADMIN, ROLES.OPERATOR];

/**
 * Roles that review manual-review cases: mirror of CS `Role.canReviewManually()` (ADR 0028). Only
 * these can be in the pool ("Recibe casos de revisión manual"); the PF service role never is.
 */
export const MANUAL_REVIEW_ROLES: readonly Role[] = [ROLES.OPERATOR, ROLES.COMMS_OPERATOR];
